import React from 'react';
import { Grid, Paper, Stack, Typography } from '@mui/material';
import { formatCurrency } from '../../../../utils/formatters';
import type { Cliente } from '../../../../services/clienteService';
import type { Info360Documento, Info360Indicadores } from '../types';
import Info360DocumentoList from './Info360DocumentoList';

interface Props {
  cliente: Cliente;
  data: Info360Indicadores;
  onAbrirDocumento?: (documento: Info360Documento) => void;
}

const MetricLine: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={600} textAlign="right">{value}</Typography>
  </Stack>
);

const Info360TabResumen: React.FC<Props> = ({ cliente, data, onAbrirDocumento }) => (
  <Grid container spacing={2}>
    <Grid item xs={12} md={6}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          Situación comercial
        </Typography>
        <Stack spacing={1}>
          <MetricLine label="Condición de venta" value={cliente.condVta?.trim() || '—'} />
          <MetricLine
            label="Lista de precios"
            value={cliente.listaPrecio?.descripcion || cliente.nroLista || '—'}
          />
          <MetricLine
            label="Vendedor"
            value={cliente.vendedorInfo?.nombreVendedor || (cliente.vendedor != null ? String(cliente.vendedor) : '—')}
          />
          <MetricLine
            label="Transporte"
            value={cliente.transporte?.nombre || cliente.transporte?.nombreExpreso || '—'}
          />
          <MetricLine label="Ventas 12 meses" value={formatCurrency(data.ventas12Meses)} />
          <MetricLine
            label="Tendencia"
            value={data.tendenciaVentasPct == null
              ? 'Sin período comparable'
              : `${data.tendenciaVentasPct > 0 ? '+' : ''}${data.tendenciaVentasPct}%`}
          />
        </Stack>
      </Paper>
    </Grid>
    <Grid item xs={12} md={6}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          Situación financiera
        </Typography>
        <Stack spacing={1}>
          <MetricLine label="Límite de crédito" value={formatCurrency(data.creditoLimite)} />
          <MetricLine label="Utilizado" value={formatCurrency(data.creditoUtilizado)} />
          <MetricLine label="Disponible" value={formatCurrency(data.creditoDisponible)} />
          <MetricLine label="Saldo total" value={formatCurrency(data.saldoTotal)} />
          <MetricLine label="Saldo vencido" value={formatCurrency(data.saldoVencido)} />
          <MetricLine
            label="Pagos en término"
            value={data.porcentajePagosEnTermino == null
              ? 'Sin datos'
              : `${data.porcentajePagosEnTermino}%`}
          />
        </Stack>
      </Paper>
    </Grid>
    <Grid item xs={12}>
      <Info360DocumentoList
        titulo="Documentos abiertos (top 5)"
        documentos={data.documentosAbiertos}
        emptyText="No hay documentos abiertos."
        onAbrirDocumento={onAbrirDocumento}
      />
    </Grid>
  </Grid>
);

export default Info360TabResumen;
